import 'objectFitPolyfill';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { IconButton, useMediaQuery } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  FileMessage,
  selectUserById,
  setImageViewerInitial,
  TwilioMessage,
} from 'models/twilio';
import {
  CloseRounded,
  CloudDownload,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import ColoredMaterialIcon from './styled/ColoredMaterialIcon';
import UserAvatar from './styled/UserAvatar';
import { ChatSubtitleText, ChatTitleSubtitleGroup, ChatTitleText } from './CommonChatDisplayComponents';
import { renderTimeOnly } from '../utils/renderTime';
import { BEHAVIOR } from '../constants';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(100% - 84px);
  background-color: black;

  @media only screen and (max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px) {
    height: calc(100% - 84px - 52px);
  }
`;

const ImageMedia = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CloseButton = React.memo(styled((props) => (
  <IconButton {...props}>
    <ColoredMaterialIcon Icon={CloseRounded} color="white" overrides={{ height: '30px', width: '30px' }} />
  </IconButton>
))`
  padding: 11px;
`, () => true);

const DesktopCloseButton = styled(CloseButton)`
  padding: 9px;
  background-color: rgba(0, 0, 0, 0.4);
`;

const DesktopInfoContainer = styled.div`
  position: absolute;
  top: 0;
  width: 100%;

  padding: 20px;
  display: flex;
  justify-content: space-between;

  @media only screen and (max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const InformationContainer = styled.div`
  height: 84px;
  width: 100%;
  padding: 20px 28px;

  display: flex;
  background-color: rgba(0, 0, 0, 0.4);

  @media only screen and (max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px) {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

const MobileHeader = styled.div`
  width: 100%;
  height: 52px;
  background-color: rgba(0, 0, 0, 0.8);

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ImageFileName = styled.div`
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 14px;
  line-height: 16px;
  height: 30px;

  background-color: rgba(0, 0, 0, 0.4);
  color: white;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledUserAvatar = styled(UserAvatar)`
  height: 44px;
  width: 44px;
`;

const StyledChatTitleSubtitleGroup = styled(ChatTitleSubtitleGroup)`
  margin-left: 12px;
  justify-content: space-evenly;

  & ${ChatTitleText} {
    color: white;
  }
  & ${ChatSubtitleText} {
    color: white;
  }
`;

const DownloadButton = styled((props) => (
  <IconButton {...props}>
    <ColoredMaterialIcon Icon={CloudDownload} color="white" />
  </IconButton>
))`
  padding: 12px 10px;

  @media only screen and (max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px) {
    padding: 14px;
  }
`;

const downloadBlob = (blob: Blob, fileName: string) => {
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, fileName);
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.setAttribute('download', fileName);

  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export interface SwipeableImageProps {
  message: TwilioMessage;
}

const SwipeableImage: FC<SwipeableImageProps> = ({ message }) => {
  const dispatch = useDispatch();
  const { author, dateCreated } = message;
  const isMobile = useMediaQuery(`(max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px)`);
  const user = useSelector(selectUserById(author));
  const { t } = useTranslation();

  const file = useMemo((): FileMessage => message.attributes?.file ?? null, [message]);
  const FileName = useMemo(() => <ImageFileName>{file?.name ?? message.id}</ImageFileName>, [message, file]);

  const closeViewer = useCallback(() => {
    dispatch(setImageViewerInitial(null));
  }, [dispatch]);

  const downloadImage = useCallback(async () => {
    try {
      const response = await fetch(file.url.replace('https://', 'https://s3.amazonaws.com/'));
      const blob = await response.blob();
      downloadBlob(blob, file.name);
    } catch (e) {
      console.error(e.message ?? e);
    }
  }, [file]);

  const onImageLoad = useCallback(() => {
    window.objectFitPolyfill();
  }, []);

  return (
    <>
      {isMobile && (
        <MobileHeader>
          <CloseButton onClick={closeViewer} />
          {FileName}
          <DownloadButton onClick={downloadImage} />
        </MobileHeader>
      )}
      <ImageContainer>
        <ImageMedia
          data-object-fit="contain"
          src={file?.url}
          alt={file?.name}
          onLoad={onImageLoad}
        />
        <DesktopInfoContainer>
          <ImageFileName>
            {file?.name ?? 'Unknown File'}
          </ImageFileName>
          <DesktopCloseButton onClick={closeViewer} />
        </DesktopInfoContainer>
      </ImageContainer>
      <InformationContainer>
        <StyledUserAvatar name={user?.name ?? author} />
        <StyledChatTitleSubtitleGroup title={user?.name ?? author} subtitle={renderTimeOnly(dateCreated, t)} />
        {!isMobile && <DownloadButton onClick={downloadImage} />}
      </InformationContainer>
    </>
  );
};

export default React.memo(SwipeableImage);
